import { inject, Service } from '@angular/core'
import { EntriesService, ExternalEntry, PurchaseEntry, RedeemEntry } from '@superlikers/microsites-api'
import { firstValueFrom, map } from 'rxjs'

import { htmlToText } from '@shared/utils'
import { Movement } from '../../models/tickets.model'

/** External donde la lambda deja cada ticket que no registró. */
const REJECTED_ACTION = 'rejected_invoices'

/** Sin paginar: el historial de un participante cabe en una sola respuesta. */
const PAGE_SIZE = 200

/** Largo de la cola del identificador que se muestra como número de ticket. */
const CODE_LENGTH = 7

/** El external solo trae `info`: el registro de la lambda serializado como JSON. */
interface RejectedData {
  info?: string
}

/** Lo que se lee del registro `failed-invoice-record-v0` de la lambda. */
interface RejectedInfo {
  record_id?: string
  image?: string
  request?: { received_at?: string; request_id?: string }
  state?: { reason_codes?: string[] }
}

/**
 * HTTP puro del historial y única pieza que sabe de dónde sale cada movimiento:
 * - las compras son los tickets aprobados, porque la lambda solo registra la
 *   compra cuando el ticket pasó;
 * - los canjes son las redenciones de premios;
 * - los rechazados son externals `rejected_invoices`, que la lambda crea con su
 *   registro del fallo.
 *
 * Los tickets (compras y rechazos) son del centro de consumo, porque la lambda
 * se los carga solo a él: se piden con su uid. Los canjes son del usuario en sesión.
 *
 * Devuelve interfaces del proyecto. Sin lógica de negocio; lanza, no maneja errores.
 */
@Service()
export class HistoryApi {
  readonly #entries = inject(EntriesService)

  async getPurchases(venueUid: string): Promise<Movement[]> {
    const purchases$ = this.#entries.listPurchases({ limit: PAGE_SIZE, participant_uid: venueUid }).pipe(map(response => response.data.map(entry => this.#purchaseAdapter(entry))))

    return firstValueFrom(purchases$)
  }

  async getRedemptions(): Promise<Movement[]> {
    const redemptions$ = this.#entries.listRedemptions({ limit: PAGE_SIZE }).pipe(map(response => response.data.map(entry => this.#redemptionAdapter(entry))))

    return firstValueFrom(redemptions$)
  }

  async getRejected(venueUid: string): Promise<Movement[]> {
    const rejected$ = this.#entries.listExternals<RejectedData>({ atype: REJECTED_ACTION, limit: PAGE_SIZE, participant_uid: venueUid }).pipe(map(response => response.data.map(entry => this.#rejectedAdapter(entry))))

    return firstValueFrom(rejected$)
  }

  /** Las botellas son la suma de las cantidades de la factura; pueden ser fracciones (copas). */
  #purchaseAdapter(entry: PurchaseEntry): Movement {
    const invoice = entry.invoice
    const bottles = (invoice?.products ?? []).reduce((total, product) => total + (Number(product.quantity) || 0), 0)

    return {
      id: entry._id,
      kind: 'purchase',
      code: this.#codeOf(invoice?.ref || entry._id),
      title: 'Ticket aprobado',
      status: 'Aprobado',
      date: this.#dateOf(invoice?.date, entry.created_at),
      amount: bottles,
      image: ''
    }
  }

  #redemptionAdapter(entry: RedeemEntry): Movement {
    return {
      id: entry._id,
      kind: 'redemption',
      code: this.#codeOf(entry._id),
      title: `Canje · ${htmlToText(entry.prize?.name ?? entry.redemption?.prize_name) || 'Premio'}`,
      status: 'Redimido',
      date: this.#dateOf(undefined, entry.created_at),
      amount: entry.redemption?.points ?? entry.prize?.points ?? 0,
      image: ''
    }
  }

  #rejectedAdapter(entry: ExternalEntry<RejectedData>): Movement {
    const info = this.#infoOf(entry.data?.info)

    return {
      id: entry._id,
      kind: 'rejected',
      code: this.#codeOf(info.request?.request_id || info.record_id || entry._id),
      title: 'Ticket rechazado',
      status: this.#reasonOf(info.state?.reason_codes ?? []),
      date: this.#dateOf(info.request?.received_at, entry.created_at),
      amount: 0,
      image: info.image ?? ''
    }
  }

  /** `info` llega como texto; si no es JSON el rechazo se muestra igual, sin detalle. */
  #infoOf(info: string | undefined): RejectedInfo {
    if (!info) return {}

    try {
      return (JSON.parse(info) as RejectedInfo | null) ?? {}
    } catch {
      return {}
    }
  }

  /** De todos los códigos de la lambda, el que el participante puede entender. */
  #reasonOf(codes: string[]): string {
    if (codes.some(code => code.startsWith('DUPLICATE'))) return 'Duplicado'
    if (codes.includes('INSUFFICIENT_OCR')) return 'Foto ilegible'

    return 'Rechazado'
  }

  /** La fecha propia del movimiento si es válida; si no, la de creación del entry (segundos). */
  #dateOf(iso: string | undefined, createdAt: number): Date {
    const date = iso ? new Date(iso) : null

    return date && !Number.isNaN(date.getTime()) ? date : new Date(createdAt * 1000)
  }

  #codeOf(id: string): string {
    return id.slice(-CODE_LENGTH).toUpperCase()
  }
}
